import { useCallback, useEffect, useState } from 'react';
import { mensagemDoErro } from '../api';
import { Aviso, Campo, Carregando, ErroDaApi, LinkProtegido, Selo, Vazio } from '../ui';
import type { Tom } from '../ui';
import {
  gerarExportacao, verCopias, verExportacoes, verInventario, verSegredosDoEscritorio,
  type EnvioAoEscritorio, type EstadoDasCopias, type EstadoDoEscritorio,
  type Exportacao as Linha, type Inventario,
} from '../api/exportacao';

/**
 * «Os dados são da Testesolo» — a tela que prova a frase.
 *
 * ── Por que ela existe ──────────────────────────────────────────────
 *
 * O §20 termina dizendo que os dados «deverão permanecer sob domínio e
 * controle da Testesolo», e pede «exportação dos dados sem dependência do
 * fornecedor». Isso é uma frase que todo fornecedor diz. A única maneira
 * de ela significar alguma coisa é existir um botão que produz o pacote,
 * hoje, sem pedir nada a ninguém.
 *
 * ── Por que o inventário aparece ANTES do botão ─────────────────────
 *
 * Porque ele é a prova de que a exportação alcança TUDO. Quem lê a lista
 * vê as tabelas todas, com contagem — e não uma seleção que alguém fez
 * por dentro e resumiu num «exportar dados». A pergunta «isso inclui os
 * ensaios de 2024?» se responde olhando, não perguntando.
 *
 * ── E por que o histórico fica à vista ──────────────────────────────
 *
 * Uma cópia de todo dado de todo cliente é a leitura mais sensível do
 * sistema. Ela não pode acontecer em silêncio: quem pediu, quando, e o
 * hash do pacote que saiu.
 */

const TOM: Record<string, Tom> = {
  concluida: 'ok', em_curso: 'atencao', falhou: 'critico',
};

const ROTULO: Record<string, string> = {
  concluida: 'Concluída', em_curso: 'Em curso', falhou: 'Falhou',
};

const numero = (v: number | string | null) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR');

/** Bytes em unidade que gente lê. Base 1000 — é a que os discos usam. */
const tamanho = (v: number | string | null) => {
  if (v == null) return '—';
  const n = Number(v);
  const u = ['B', 'kB', 'MB', 'GB', 'TB'];
  let i = 0;
  let x = n;
  while (x >= 1000 && i < u.length - 1) { x /= 1000; i += 1; }
  return `${x.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${u[i]}`;
};

const quando = (v: string | null) =>
  v ? new Date(v).toLocaleString('pt-BR') : '—';

/** «1 arquivo», «12 arquivos». O plural é detalhe, e detalhe é o acabamento. */
const plural = (n: number, um: string, muitos: string) =>
  `${numero(n)} ${n === 1 ? um : muitos}`;

/** `2026-10-07_0317` → `07/10/2026 03:17` (o nome já é horário de Brasília). */
const nomeDaCopia = (n: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})_(\d{2})(\d{2})(-\d+)?$/.exec(n);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}${m[6] ? ` (${m[6].slice(1)}ª)` : ''}` : n;
};

const SITUACAO_DA_COPIA: Record<string, { tom: Tom; rotulo: string }> = {
  ok: { tom: 'ok', rotulo: 'Em dia' },
  atencao: { tom: 'atencao', rotulo: 'Em dia, com observações' },
  falha: { tom: 'critico', rotulo: 'Falhou' },
};

/**
 * A cópia de segurança diária — onda 297 · RN-244.
 *
 * Fica nesta tela porque é a mesma pergunta vista de outro lado: «os
 * dados da Testesolo estão a salvo?». A exportação responde «dá para
 * levar»; a cópia responde «dá para voltar». E a resposta mostra a PROVA —
 * quantas tabelas e linhas voltaram na restauração de teste —, não só
 * «backup ok», que é a frase que todo backup quebrado também diz.
 */
/** «há 3 h 12 min» — duração de algo que ainda está de pé. */
const ha = (s: number | null | undefined) => {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h >= 48) return `há ${Math.floor(h / 24)} dias`;
  return h ? `há ${h} h ${m} min` : `há ${m} min`;
};

/**
 * A cópia no escritório — onda 299 · RN-246.
 *
 * A segunda cópia, a que sobrevive à perda do servidor: o Dream Machine do
 * escritório abre uma VPN até o servidor, e toda noite a cópia PROVADA vai
 * para o UNAS. Enquanto a primeira não chegou, a tela mostra o que digitar no
 * UniFi — inclusive a chave do túnel e a senha do UNAS, que nasceram no
 * servidor e somem daqui quando a primeira cópia chega.
 */
function CopiaNoEscritorio({ esc, envio }: {
  esc: EstadoDoEscritorio; envio: EnvioAoEscritorio | null | undefined;
}) {
  const [segredos, setSegredos] = useState<{ chaveDoTunel: string; senhaDoUnas: string | null } | null>(null);
  const [recado, setRecado] = useState<string | null>(null);

  const mostrar = async () => {
    setRecado(null);
    try { setSegredos(await verSegredosDoEscritorio()); }
    catch (e) { setRecado(mensagemDoErro(e)); }
  };
  const copiar = (v: string) => { void navigator.clipboard?.writeText(v); };

  const sit: { tom: Tom; rotulo: string } = esc.instalando
    ? { tom: 'andamento', rotulo: 'Instalando o túnel no servidor' }
    : esc.segredosPendentes
    ? { tom: 'atencao', rotulo: 'Aguardando a configuração no UniFi' }
    : !esc.tunel.conectado
      ? { tom: 'critico', rotulo: 'Túnel desconectado' }
      : envio?.situacao === 'ok'
        ? { tom: 'ok', rotulo: 'Em dia' }
        : { tom: 'atencao', rotulo: 'Túnel de pé; a cópia da noite não chegou' };
  const u = esc.unifi;

  return (
    <div>
      <h3>
        Cópia no escritório (UNAS)
        {' '}<Selo tom={sit.tom}>{sit.rotulo}</Selo>
      </h3>

      <div className="placar-simples">
        <div>
          <span className="n">{esc.tunel.conectado ? 'Conectado' : 'Desconectado'}</span>
          <span className="q">
            túnel com o escritório
            {esc.tunel.conectado ? ` (${ha(esc.tunel.conectadoHaS)})` : ''}
          </span>
        </div>
        <div>
          <span className="n">{envio?.ultimoSucessoEm ? quando(envio.ultimoSucessoEm) : 'Nenhuma ainda'}</span>
          <span className="q">
            última cópia que chegou e conferiu
            {envio?.ultimaConferenciaCompletaEm
              ? ` · tudo reconferido em ${quando(envio.ultimaConferenciaCompletaEm)}` : ''}
          </span>
        </div>
        <div>
          <span className="n">{esc.unas.compartilhamento}</span>
          <span className="q" title={esc.unas.caminho}>
            pasta no UNAS ({esc.unas.ip}), em {esc.unas.subpasta}
          </span>
        </div>
      </div>

      {envio && envio.situacao !== 'ok' && envio.erro && !esc.segredosPendentes && (
        <Aviso tipo="atencao" titulo="A cópia da última noite não chegou">
          <p>{envio.erro}</p>
        </Aviso>
      )}
      {esc.erro && <Aviso tipo="atencao" titulo="Manutenção do túnel">{esc.erro}</Aviso>}

      {esc.segredosPendentes && (
        <>
          <p>
            O servidor já está pronto e esperando. Falta o escritório: criar no
            UniFi a VPN site a site com os valores abaixo, a regra de firewall
            que só deixa o túnel chegar ao UNAS, e o usuário da cópia no UniFi
            Drive com acesso à pasta {esc.unas.compartilhamento}. O passo a
            passo está no doc 282.
          </p>
          <table className="dados">
            <thead><tr><th>No UniFi Network (VPN site a site)</th><th>Valor</th></tr></thead>
            <tbody>
              <tr><td>Tipo</td><td>{u.tipo}</td></tr>
              <tr><td>IP remoto (o servidor)</td><td className="mono">{u.ipDoServidor}</td></tr>
              <tr><td>Rede remota</td><td className="mono">{u.redeRemota}</td></tr>
              <tr><td>Rede local que o túnel alcança</td><td className="mono">{u.redeLocal}</td></tr>
              <tr><td>IKE / criptografia / hash / grupo DH</td>
                <td>{u.ike} · {u.criptografia} · {u.hash} · DH {u.grupoDh}{u.pfs ? ' · PFS ligado' : ''}</td></tr>
              <tr><td>Vida da fase 1 / fase 2</td><td>{numero(u.vidaIkeS)} s · {numero(u.vidaEspS)} s</td></tr>
              <tr><td>Única porta liberada até o UNAS</td><td>{u.portaNoUnas}</td></tr>
              <tr><td>Usuário da cópia no UNAS</td><td className="mono">{esc.unas.usuario}</td></tr>
            </tbody>
          </table>

          {!segredos ? (
            <div className="acoes">
              <button type="button" className="secundario" onClick={() => void mostrar()}>
                Mostrar a chave do túnel e a senha do UNAS
              </button>
            </div>
          ) : (
            <table className="dados">
              <tbody>
                <tr>
                  <td>Chave pré-compartilhada do túnel</td>
                  <td className="mono">{segredos.chaveDoTunel}</td>
                  <td><button type="button" className="secundario"
                              onClick={() => copiar(segredos.chaveDoTunel)}>Copiar</button></td>
                </tr>
                {segredos.senhaDoUnas && (
                  <tr>
                    <td>Senha do usuário {esc.unas.usuario}</td>
                    <td className="mono">{segredos.senhaDoUnas}</td>
                    <td><button type="button" className="secundario"
                                onClick={() => copiar(segredos.senhaDoUnas ?? '')}>Copiar</button></td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {recado && <Aviso tipo="erro">{recado}</Aviso>}
          <p className="det">
            A chave e a senha nasceram no servidor e só aparecem aqui até a
            primeira cópia chegar ao UNAS. Depois disso, somem desta tela.
          </p>
        </>
      )}
    </div>
  );
}

function CopiasDeSeguranca({ estado }: { estado: EstadoDasCopias | null }) {
  if (!estado) return null;
  const u = estado.ultimo;
  const parada = estado.avisos.some((a) => a.nivel === 'critico' && /há mais de/.test(a.texto));
  const sit = parada
    ? { tom: 'critico' as Tom, rotulo: 'Parada' }
    : u ? SITUACAO_DA_COPIA[u.situacao] ?? { tom: 'neutro' as Tom, rotulo: u.situacao }
      : { tom: 'critico' as Tom, rotulo: 'Nunca rodou' };

  return (
    <section id="copias">
      <h2>
        Cópia de segurança diária
        {' '}<Selo tom={sit.tom}>{sit.rotulo}</Selo>
      </h2>

      {estado.avisos.map((a) => (
        <Aviso key={a.texto} tipo={a.nivel === 'critico' ? 'erro' : 'atencao'}>{a.texto}</Aviso>
      ))}

      {u && (
        <>
          <div className="placar-simples">
            <div><span className="n">{quando(u.terminadaEm)}</span>
              <span className="q">última cópia</span></div>
            <div>
              <span className="n">
                {u.restauracao?.ok ? 'Conferida' : u.restauracao ? 'Não passou' : '—'}
              </span>
              <span className="q">
                {u.restauracao?.ok
                  ? `restaurada: ${numero(u.restauracao.tabelasConferidas ?? 0)} tabelas, `
                    + `${numero(u.restauracao.linhasConferidas ?? 0)} linhas iguais`
                  : 'prova de restauração'}
              </span>
            </div>
            <div><span className="n">{tamanho(u.banco?.bytes ?? null)}</span>
              <span className="q">banco comprimido</span></div>
            <div><span className="n">{numero(u.arquivos?.copiados ?? null)}</span>
              <span className="q">
                arquivos copiados
                {u.arquivos ? ` (${tamanho(u.arquivos.bytes)})` : ''}
              </span></div>
            <div><span className="n">{tamanho(u.disco?.livreBytes ?? null)}</span>
              <span className="q">livres no disco</span></div>
          </div>

          {u.motivos.length > 0 && (
            <Aviso tipo={u.situacao === 'falha' ? 'erro' : 'atencao'}
                   titulo="Observações da última cópia">
              <ul>{u.motivos.map((m) => <li key={m.texto}>{m.texto}</li>)}</ul>
            </Aviso>
          )}

          <h3>Cópias guardadas</h3>
          {u.copias.length === 0 ? (
            <Vazio>Nenhuma cópia do banco guardada.</Vazio>
          ) : (
            <table className="dados">
              <thead>
                <tr>
                  <th>Cópia</th><th>Prova</th><th className="num">Linhas</th>
                  <th>Migração até</th><th className="num">Tamanho</th>
                </tr>
              </thead>
              <tbody>
                {u.copias.map((c) => (
                  <tr key={c.nome}>
                    <td>{nomeDaCopia(c.nome)}</td>
                    <td>
                      <Selo tom={c.verificada ? 'ok' : 'critico'}>
                        {c.verificada ? 'Restaurada e conferida' : 'Não conferida'}
                      </Selo>
                    </td>
                    <td className="num">{numero(c.linhas)}</td>
                    <td className="mono">{c.migracaoAte?.slice(0, 4) ?? '—'}</td>
                    <td className="num">{tamanho(c.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {estado.historico.length > 1 && (
        <>
          <h3>As últimas noites</h3>
          <table className="dados">
            <thead><tr><th>Quando</th><th>Situação</th><th>Observações</th></tr></thead>
            <tbody>
              {estado.historico.slice(0, 7).map((h) => (
                <tr key={`${h.copia}-${h.terminadaEm}`}>
                  <td>{quando(h.terminadaEm)}</td>
                  <td>
                    <Selo tom={SITUACAO_DA_COPIA[h.situacao]?.tom ?? 'neutro'}>
                      {SITUACAO_DA_COPIA[h.situacao]?.rotulo ?? h.situacao}
                    </Selo>
                  </td>
                  <td>{h.motivos.length ? h.motivos.join(' ') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {estado.escritorio && (
        <CopiaNoEscritorio esc={estado.escritorio} envio={u?.escritorio} />
      )}

      <Aviso tipo="info" titulo="Onde ficam e do que protegem">
        <p>
          Todo dia às 03:17 o servidor copia o banco inteiro e os arquivos
          novos (fotos, PDFs, certificados), e <strong>restaura a cópia
          do banco num banco de teste</strong> para conferir, tabela por
          tabela, que tudo voltou. Cópia que não passa nessa prova não conta
          e não autoriza apagar nenhuma anterior.
        </p>
        <p>
          Ficam as cópias dos últimos 14 dias e a primeira de cada mês, por
          12 meses. Os arquivos nunca são apagados da cópia, nem quando
          somem do armazenamento.
        </p>
        <p>
          <strong>As cópias do servidor</strong> protegem contra erro de
          lançamento, migração que estrague dado e atualização do
          PostgreSQL que dê errado — <strong>não</strong>{' '}
          contra a perda do servidor. Para isso existe a{' '}
          <strong>cópia no escritório</strong>: toda noite em que a prova
          passa, a mesma cópia vai para o UNAS, por um túnel que o próprio
          escritório abre, e lá nada é apagado.
        </p>
      </Aviso>
    </section>
  );
}

export function Exportacao() {
  const [inv, setInv] = useState<Inventario | null>(null);
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  const [observacoes, setObservacoes] = useState('');
  const [verTabelas, setVerTabelas] = useState(false);
  const [copias, setCopias] = useState<EstadoDasCopias | null>(null);

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      const [i, h] = await Promise.all([verInventario(), verExportacoes()]);
      setInv(i);
      setLinhas(h);
    } catch (e) { setErro(e); }
    // O estado da cópia não derruba a tela: sem ele, a exportação segue.
    verCopias().then(setCopias, () => setCopias(null));
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const gerar = async () => {
    setGerando(true);
    setRecado(null);
    try {
      const r = await gerarExportacao(
        { observacoes: observacoes.trim() || undefined });
      setRecado(
        `Pacote pronto: ${numero(r.linhas)} linhas de ${r.tabelas} tabelas, `
        + `${tamanho(r.tamanho_bytes)}.`);
      setObservacoes('');
      await carregar();
    } catch (e) {
      setRecado(mensagemDoErro(e));
    } finally { setGerando(false); }
  };

  if (erro) return <ErroDaApi erro={erro} />;
  if (!inv) return <Carregando texto="Contando o que a casa tem" />;

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1>Exportação dos dados</h1>
          <p className="sub">
            Uma cópia completa, em formato aberto, que não depende de mim
            nem da hospedagem para ser lida
          </p>
        </div>
      </div>

      {copias?.avisos.some((a) => a.nivel === 'critico') && (
        <Aviso tipo="erro" titulo="A cópia de segurança precisa de atenção">
          <p>
            {copias.avisos.find((a) => a.nivel === 'critico')?.texto}
            {' '}<a href="#copias" onClick={(e) => {
              e.preventDefault();
              document.getElementById('copias')?.scrollIntoView({ behavior: 'smooth' });
            }}>Ver a cópia de segurança</a>
          </p>
        </Aviso>
      )}

      <Aviso tipo="info" titulo="O que sai no pacote">
        <p>
          Todas as {inv.totalTabelas} tabelas do banco, uma linha por
          registro, em <strong>JSON Lines comprimido com gzip</strong> —
          formato de texto que qualquer linguagem lê e que se abre com
          <code> zcat</code> num terminal. O esquema são as migrações do
          repositório da Testesolo: rodar as migrações num PostgreSQL
          vazio e carregar estas linhas reconstrói a casa inteira.
        </p>
        <p>
          <strong>Não</strong> vão os bytes das fotografias, PDFs e
          certificados — são {tamanho(inv.arquivosBytes)} em
          {' '}{plural(inv.arquivos, 'arquivo', 'arquivos')}, e isso não
          sai por uma página web. Vai o inventário de cada um, com bucket, chave,
          tamanho e hash, que é o que permite baixá-los depois e conferir
          peça por peça se chegaram inteiros.
        </p>
        <p>
          <strong>Não</strong> vão as senhas nem os segredos de segundo
          fator. Quem restaurar redefine as senhas — que é o que se faria
          de qualquer jeito.
        </p>
      </Aviso>

      <div className="placar-simples">
        <div><span className="n">{inv.totalTabelas}</span>
          <span className="q">tabelas</span></div>
        <div><span className="n">{numero(inv.totalLinhas)}</span>
          <span className="q">linhas</span></div>
        <div><span className="n">{numero(inv.arquivos)}</span>
          <span className="q">
            {inv.arquivos === 1 ? 'arquivo' : 'arquivos'}
            {' '}({tamanho(inv.arquivosBytes)})
          </span></div>
        <div><span className="n">{inv.migracaoAte?.slice(0, 4) ?? '—'}</span>
          <span className="q">última migração aplicada</span></div>
      </div>

      <p>
        <button type="button" className="secundario"
                onClick={() => setVerTabelas((v) => !v)}>
          {verTabelas ? 'Ocultar' : 'Ver'} a lista das {inv.totalTabelas} tabelas
        </button>
      </p>

      {verTabelas && (
        <div className="rolar-y">
          <table className="dados">
            <thead><tr><th>Tabela</th><th className="num">Linhas</th></tr></thead>
            <tbody>
              {inv.tabelas.map((t) => (
                <tr key={t.tabela}>
                  <td>{t.tabela}</td>
                  <td className="num">{numero(t.linhas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {recado && <Aviso tipo="info">{recado}</Aviso>}

      <h2>Gerar agora</h2>
      <Campo
        rotulo="Para que é esta cópia?"
        value={observacoes}
        onChange={(e) => setObservacoes(e.target.value)}
        dica="Fica registrado junto do pedido. Uma cópia de todos os dados
              de todos os clientes não deve sair sem alguém dizer por quê."
      />
      <div className="acoes">
        <button type="button" onClick={() => void gerar()} disabled={gerando}>
          {gerando ? 'Gerando o pacote…' : 'Gerar a exportação'}
        </button>
      </div>

      <h2>Quem já pediu</h2>
      {linhas.length === 0 ? (
        <Vazio>Nenhuma exportação pedida ainda.</Vazio>
      ) : (
        <table className="dados">
          <thead>
            <tr>
              <th>Quando</th><th>Quem</th><th>Situação</th>
              <th className="num">Linhas</th><th className="num">Tamanho</th>
              <th>Pacote</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.id}>
                <td>{quando(l.pedida_em)}</td>
                <td>
                  {l.pedida_por_nome ?? '—'}
                  {l.observacoes && (
                    <small className="det linha-de-baixo">{l.observacoes}</small>
                  )}
                </td>
                <td>
                  <Selo tom={TOM[l.situacao] ?? 'neutro'}
                        titulo={l.erro ?? undefined}>
                    {ROTULO[l.situacao] ?? l.situacao}
                  </Selo>
                </td>
                <td className="num">{numero(l.linhas)}</td>
                <td className="num">{tamanho(l.tamanho_bytes)}</td>
                <td>
                  {l.arquivo_id ? (
                    <LinkProtegido caminho={`/arquivos/${l.arquivo_id}/conteudo`}
                                   nomeQueda={l.pacote ?? 'exportacao.jsonl.gz'}>
                      Baixar
                    </LinkProtegido>
                  ) : '—'}
                  {l.hash_sha256 && (
                    <small className="det linha-de-baixo mono"
                           title={`SHA-256: ${l.hash_sha256}`}>
                      {l.hash_sha256.slice(0, 16)}…
                    </small>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <CopiasDeSeguranca estado={copias} />
    </div>
  );
}
